import { Mail, Scale, Shield, Info, MessageSquare } from 'lucide-react';
import { type FC } from 'react';
import { Link } from 'react-router-dom';

/**
 * Footer - Application footer with legal links and company info
 *
 * Displays links to privacy policy, terms and conditions, and contact information.
 */
export const Footer: FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-bg-secondary border-t border-border-subtle mt-auto">
      <div className="container-app py-8">
        <div className="max-w-6xl mx-auto">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
            {/* About section */}
            <div>
              <h2 className="text-lg font-display font-bold text-accent-primary mb-3 uppercase tracking-wide">
                Grim Dark Companion
              </h2>
              <p className="text-sm text-text-secondary leading-relaxed">
                AI-powered battle assistant for tabletop miniature games. Track battles, get rules help, and transform your games into epic narratives.
              </p>
            </div>

            {/* Legal links */}
            <div>
              <h2 className="text-lg font-semibold text-text-primary mb-3">Legal</h2>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-sm text-text-secondary hover:text-accent-primary transition-colors flex items-center gap-2 min-h-[32px]"
                  >
                    <Shield size={16} />
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-and-conditions"
                    className="text-sm text-text-secondary hover:text-accent-primary transition-colors flex items-center gap-2 min-h-[32px]"
                  >
                    <Scale size={16} />
                    Terms & Conditions
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-sm text-text-secondary hover:text-accent-primary transition-colors flex items-center gap-2 min-h-[32px]"
                  >
                    <Info size={16} />
                    About
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact section */}
            <div>
              <h2 className="text-lg font-semibold text-text-primary mb-3">Contact</h2>
              <ul className="space-y-2">
                <li>
                  <a
                    href="mailto:privacy@ozdyne.com"
                    className="text-sm text-text-secondary hover:text-accent-primary transition-colors flex items-center gap-2 min-h-[32px]"
                  >
                    <Mail size={16} />
                    privacy@ozdyne.com
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:legal@ozdyne.com"
                    className="text-sm text-text-secondary hover:text-accent-primary transition-colors flex items-center gap-2 min-h-[32px]"
                  >
                    <Mail size={16} />
                    legal@ozdyne.com
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:grimdarkcompanion@ozdyne.com"
                    className="text-sm text-text-secondary hover:text-accent-primary transition-colors flex items-center gap-2 min-h-[32px]"
                  >
                    <MessageSquare size={16} />
                    Feedback & Feature Requests
                  </a>
                </li>
              </ul>

              {/* Social Media Links */}
              <div className="mt-4 flex items-center gap-2">
                <a
                  href="https://x.com/grimdarkcomp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] opacity-70 hover:opacity-100 transition-opacity"
                  aria-label="Follow us on X (Twitter)"
                >
                  <img src="/x-logo.png" alt="X (Twitter)" className="w-6 h-6" />
                </a>
                <a
                  href="https://instagram.com/grimdarkcompanion"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] opacity-70 hover:opacity-100 transition-opacity"
                  aria-label="Follow us on Instagram"
                >
                  <img src="/insta-logo.png" alt="Instagram" className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-6 border-t border-border-subtle">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-text-muted">
                &copy; {currentYear} OzDyne Ltd. All rights reserved.
              </p>
              <p className="text-xs text-text-muted text-center md:text-right max-w-md">
                This is an independent fan project. Not affiliated with or endorsed by any game publisher.
                All game names and trademarks are property of their respective owners.
              </p>
            </div>
          </div>

          {/* Privacy note */}
          <div className="mt-4 text-center space-y-1">
            <p className="text-xs text-text-muted">
              Your data is private and never used to train AI models.
            </p>
            <p className="text-xs text-text-muted">
              We provide companion tools only. No copyrighted game rules or content are stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
